import classNames from 'classnames';
import React, { useMemo } from 'react';
import type { Column } from 'react-table';

import { UiBuilding } from '@vyaguta/vyaguta-icons/ui';

import Table from 'components/common/table';
import RemoteIcon from 'components/common/remoteIcon';

import { DAYS_MAPPING, DDDD, WEEK_MONTH_DAY_FORMAT } from 'constants/date';
import { WORK_LOCATION_SHORT, workShiftIcon, WORK_SHIFT_LABELS } from 'constants/worklog';

import { getFormattedDate } from 'utils/date';
import { toTextComponent } from 'utils/component';
import { formatEmployeeAvailabilityTime } from 'utils/user';

type WorkShiftKey = keyof typeof WORK_SHIFT_LABELS;

type EmployeeRosterRow = {
  date?: string;
  workDate?: string;
  workShift?: WorkShiftKey;
  availabilityTime?: {
    startTime: string;
    startTimeType: string;
    endTime: string;
    endTimeType: string;
  };
  location?: string;
};

type TableRowInfo = {
  index?: number;
  original?: EmployeeRosterRow;
};

type CellProps = {
  original: EmployeeRosterRow;
};

type Props = {
  rosterData?: EmployeeRosterRow[];
  isLoading?: boolean;
};

export function EmployeeRosterTable(props: Props) {
  const { rosterData = [], isLoading = false } = props;

  const getTableRowsProps = (_state: object, rowInfo: TableRowInfo) => {
    const day = getFormattedDate(rowInfo?.original?.date, DDDD);

    const styledClassname = classNames({
      'separator-row': day === DAYS_MAPPING.SUNDAY,
      'first-row': rowInfo?.index === 0,
    });

    return {
      className: styledClassname.trim(),
    };
  };

  const employeeRosterColumns = useMemo<Array<Column<EmployeeRosterRow>>>(() => {
    const clockinHistoryColumns: Array<Column<EmployeeRosterRow>> = [
      {
        Header: 'Day',
        sortable: false,
        width: 140,
        resizable: false,
        accessor: 'day',
        Cell: ({ original }: CellProps) => {
          const { workDate } = original;

          const day = getFormattedDate(workDate, WEEK_MONTH_DAY_FORMAT);

          return <span>{day}</span>;
        },
      },
      {
        Header: 'Shift',
        sortable: false,
        width: 140,
        resizable: false,
        accessor: 'shift',
        Cell: ({ original }: CellProps) => {
          const { workShift } = original;

          if (workShift) {
            return (
              <div className="d-flex align-items-center gap-px-4">
                <span>{workShiftIcon[workShift]}</span>
                <span>{WORK_SHIFT_LABELS[workShift]}</span>
              </div>
            );
          }

          return '-';
        },
      },
      {
        Header: 'Availability Time',
        sortable: false,
        width: 200,
        resizable: false,
        accessor: 'availabilityTime',
        Cell: ({ original }: CellProps) => {
          const { availabilityTime } = original;

          if (availabilityTime) {
            const formattedAvailabilityTime = formatEmployeeAvailabilityTime(availabilityTime);

            const TextComponent = toTextComponent(formattedAvailabilityTime);
            return <span>{typeof TextComponent === 'function' ? <TextComponent /> : TextComponent}</span>;
          }

          return '-';
        },
      },
      {
        Header: 'Roster',
        sortable: false,
        width: 110,
        resizable: false,
        accessor: 'roster',
        Cell: ({ original }: CellProps) => {
          const { location } = original;

          if (location === WORK_LOCATION_SHORT.OFFICE) {
            return (
              <span className="-mlpx-1 hpx-18 wpx-18">
                <UiBuilding size={18} color="#102b7b" />
              </span>
            );
          }

          if (location === WORK_LOCATION_SHORT.HOME) {
            return (
              <span className="-mlpx-2 hpx-18 wpx-18">
                <RemoteIcon size={18} color="#102b7b" />
              </span>
            );
          }

          return '-';
        },
      },
    ];

    return clockinHistoryColumns;
  }, []);

  return (
    <Table
      columns={employeeRosterColumns}
      showPagination={false}
      sortable={false}
      customClassName="employee-roster-table rt-cursor-default"
      getTrProps={getTableRowsProps}
      data={rosterData}
      getTbodyProps={() => {
        return { className: 'custom-scroll-bar' };
      }}
      loading={isLoading}
    />
  );
}
